import React, { useState, useEffect, useCallback } from 'react';
import {
  login,
  logout,
  isLoggedIn,
  getStoredEmail,
  fetchTemplate,
  saveTemplate,
  fetchPreview,
  testBroadcast,
  fetchPlatformAvailability,
  fetchLatestItems,
} from './api';
import LoginScreen from './components/LoginScreen';
import SectionList from './components/SectionList';
import ColumnConfig from './components/ColumnConfig';
import ItemEditor from './components/ItemEditor';
import Preview from './components/Preview';
import PlatformToggles from './components/PlatformToggles';
import WhatsAppConnect from './components/WhatsAppConnect';
import ScheduleConfig from './components/ScheduleConfig';
import ExcludedDates from './components/ExcludedDates';
import './App.css';

const DEFAULT_PLATFORMS = { telegram: true, whatsapp: true, facebook: true };
const DEFAULT_SCHEDULE = {
  enabled: true,
  time: '09:00',
  timezone: 'Europe/Ljubljana',
  days: [1, 2, 3, 4, 5],
  excluded_dates: [],
};

const ALL_COLUMNS = [
  { key: 'name', label: 'Naziv' },
  { key: 'weight', label: 'Neto masa' },
  { key: 'buyPrice', label: 'Nakup' },
  { key: 'pricePerGram', label: 'Cena / g' },
  { key: 'sellPrice', label: 'Odkup' },
  { key: 'sellPricePerGram', label: 'Cena/g (odkupa)' },
];

export default function App() {
  const [authed, setAuthed] = useState(isLoggedIn());
  const [userEmail, setUserEmail] = useState(getStoredEmail());
  const [template, setTemplate] = useState(null);
  const [preview, setPreview] = useState('');
  const [previewDate, setPreviewDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [status, setStatus] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [platformAvailability, setPlatformAvailability] = useState(null);
  const [latestItems, setLatestItems] = useState({});

  const handleAuthError = useCallback(() => {
    logout();
    setAuthed(false);
    setUserEmail('');
    setTemplate(null);
    setPreview('');
  }, []);

  const loadTemplate = useCallback(async () => {
    try {
      const t = await fetchTemplate();
      setTemplate({
        ...t,
        platforms: { ...DEFAULT_PLATFORMS, ...(t.platforms || {}) },
        schedule_config: { ...DEFAULT_SCHEDULE, ...(t.schedule_config || {}) },
        items_config: t.items_config || {},
      });
    } catch (err) {
      if (err.message === 'AUTH') handleAuthError();
    }
  }, [handleAuthError]);

  const loadLatestItems = useCallback(async () => {
    try {
      const res = await fetchLatestItems();
      setLatestItems(res.items || {});
    } catch (err) {
      if (err.message === 'AUTH') handleAuthError();
    }
  }, [handleAuthError]);

  const loadAvailability = useCallback(async () => {
    try {
      const a = await fetchPlatformAvailability();
      setPlatformAvailability(a);
    } catch (err) {
      if (err.message === 'AUTH') handleAuthError();
    }
  }, [handleAuthError]);

  const loadPreview = useCallback(async (tmpl) => {
    try {
      const res = await fetchPreview(tmpl);
      setPreview(res.plain || '');
      setPreviewDate(res.scraped_at || '');
    } catch {
      setPreview('No price data available yet.');
    }
  }, []);

  useEffect(() => {
    if (authed) {
      loadTemplate();
      loadAvailability();
      loadLatestItems();
    }
  }, [authed, loadTemplate, loadAvailability, loadLatestItems]);

  useEffect(() => {
    if (template) loadPreview(template);
  }, [template, loadPreview]);

  const handleLogin = async (email, password) => {
    const data = await login(email, password);
    setUserEmail(data.user.email);
    setAuthed(true);
  };

  const handleLogout = () => {
    handleAuthError();
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const saved = await saveTemplate(template);
      setTemplate({
        ...saved,
        platforms: { ...DEFAULT_PLATFORMS, ...(saved.platforms || {}) },
        schedule_config: { ...DEFAULT_SCHEDULE, ...(saved.schedule_config || {}) },
        items_config: saved.items_config || {},
      });
      setStatus({ type: 'success', text: 'Template saved!' });
    } catch (err) {
      if (err.message === 'AUTH') { handleAuthError(); return; }
      setStatus({ type: 'error', text: 'Save failed: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleBroadcast = async () => {
    if (!window.confirm('Send prices to all enabled platforms now?')) return;
    setBroadcasting(true);
    setStatus(null);
    try {
      const result = await testBroadcast();
      if (result.anySuccess) {
        setStatus({ type: 'success', text: 'Broadcast sent!' });
      } else {
        setStatus({ type: 'error', text: 'Broadcast failed on all platforms.' });
      }
    } catch (err) {
      if (err.message === 'AUTH') { handleAuthError(); return; }
      setStatus({ type: 'error', text: 'Broadcast error: ' + err.message });
    } finally {
      setBroadcasting(false);
    }
  };

  const updateField = (field, value) => {
    setTemplate((t) => ({ ...t, [field]: value }));
  };

  const updateSections = (sections) => {
    setTemplate((t) => ({ ...t, sections }));
  };

  const updateColumnConfig = (sectionKey, columns) => {
    setTemplate((t) => ({
      ...t,
      column_config: { ...t.column_config, [sectionKey]: columns },
    }));
  };

  const updateItemsConfig = (sectionKey, cfg) => {
    setTemplate((t) => ({
      ...t,
      items_config: { ...(t.items_config || {}), [sectionKey]: cfg },
    }));
  };

  const updatePlatforms = (platforms) => {
    setTemplate((t) => ({ ...t, platforms }));
  };

  const updateSchedule = (patch) => {
    setTemplate((t) => ({
      ...t,
      schedule_config: { ...DEFAULT_SCHEDULE, ...(t.schedule_config || {}), ...patch },
    }));
  };

  const updateExcludedDates = (excluded_dates) => {
    updateSchedule({ excluded_dates });
  };

  if (!authed) return <LoginScreen onLogin={handleLogin} />;
  if (!template) return <div className="loading">Loading...</div>;

  return (
    <div className="app">
      <header className="app-header">
        <h1>Zlatarenta Price Bot</h1>
        <span className="subtitle">Message Template Editor</span>
        <div className="header-right">
          <span className="user-email">{userEmail}</span>
          <button className="btn-link" onClick={handleLogout}>Sign Out</button>
        </div>
      </header>

      <div className="app-body">
        <div className="editor-panel">
          <section className="card">
            <h2>Header & Footer</h2>
            <label>
              Header Text
              <input
                type="text"
                value={template.header_text || ''}
                onChange={(e) => updateField('header_text', e.target.value)}
              />
            </label>
            <label>
              Footer / Unsubscribe
              <textarea
                rows={3}
                value={template.footer_text || ''}
                onChange={(e) => updateField('footer_text', e.target.value)}
              />
            </label>
          </section>

          <section className="card">
            <h2>Sections</h2>
            <p className="hint">Drag to reorder. Toggle to enable/disable. Click to edit columns.</p>
            <SectionList
              sections={template.sections || []}
              onChange={updateSections}
              expandedSection={expandedSection}
              onToggleExpand={(key) =>
                setExpandedSection(expandedSection === key ? null : key)
              }
            />
            {expandedSection && (
              <>
                <ColumnConfig
                  sectionKey={expandedSection}
                  allColumns={ALL_COLUMNS}
                  activeColumns={template.column_config?.[expandedSection] || []}
                  onChange={(cols) => updateColumnConfig(expandedSection, cols)}
                />
                <ItemEditor
                  sectionKey={expandedSection}
                  sectionLabel={
                    template.sections?.find((s) => s.key === expandedSection)?.label ||
                    expandedSection
                  }
                  fetchedItems={latestItems[expandedSection] || []}
                  config={template.items_config?.[expandedSection] || { hidden: [], extras: [] }}
                  onChange={(cfg) => updateItemsConfig(expandedSection, cfg)}
                />
              </>
            )}
          </section>

          <section className="card">
            <h2>Broadcast Platforms</h2>
            <p className="hint">
              Toggle which platforms receive the scheduled broadcast. A platform must be
              credentialed in the environment to actually send.
            </p>
            <PlatformToggles
              platforms={template.platforms}
              availability={platformAvailability}
              onChange={updatePlatforms}
            />
          </section>

          <section className="card">
            <h2>WhatsApp Connection</h2>
            <p className="hint">
              Link the bot to WhatsApp by scanning the QR with your phone. This is
              normally a one-time step — the session persists across restarts.
            </p>
            <WhatsAppConnect onAuthError={handleAuthError} />
          </section>

          <section className="card">
            <h2>Schedule</h2>
            <p className="hint">Set the time and weekdays for the automatic broadcast.</p>
            <ScheduleConfig
              schedule={template.schedule_config}
              onChange={(next) =>
                setTemplate((t) => ({ ...t, schedule_config: next }))
              }
            />
          </section>

          <section className="card">
            <h2>Excluded Dates</h2>
            <p className="hint">
              Add individual dates (holidays, custom skips) when the bot should not
              broadcast even if it's a scheduled day.
            </p>
            <ExcludedDates
              dates={template.schedule_config?.excluded_dates || []}
              onChange={updateExcludedDates}
            />
          </section>

          <div className="actions">
            <button className="btn primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button className="btn secondary" onClick={handleBroadcast} disabled={broadcasting}>
              {broadcasting ? 'Sending...' : 'Test Broadcast'}
            </button>
          </div>

          {status && (
            <div className={`status-bar ${status.type}`}>{status.text}</div>
          )}
        </div>

        <div className="preview-panel">
          <Preview text={preview} scrapedAt={previewDate} />
        </div>
      </div>
    </div>
  );
}
